import { useState } from "react";
import { toast } from "sonner";
import { Globe, Loader2, EyeOff, Eye, Users, MessageSquare, Fingerprint, Camera, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LANGS } from "@/lib/babel";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";

function useProfilePatch() {
  const { refreshUser } = useAuth();
  const [busy, setBusy] = useState(null);
  const patch = async (field, value, okMsg) => {
    setBusy(field);
    try {
      await api.updateProfile({ [field]: value });
      await refreshUser();
      if (okMsg) toast.success(okMsg);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((typeof d === "string" && d) || d?.message || "Couldn't save that setting.");
    } finally {
      setBusy(null);
    }
  };
  return { patch, busy };
}

// §16.9 Language picker + "Auto-translate every chat" default (cc_24 auto_translate_all).
export function LanguageCard() {
  const { user } = useAuth();
  const { patch, busy } = useProfilePatch();
  const lang = (user?.preferred_lang || "en").slice(0, 2);
  return (
    <div id="language-card" className="rounded-3xl border border-white/8 bg-bloq-s1 p-5" data-testid="language-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
        <Globe className="h-4 w-4 text-bloq-blue" /> Language & Babel
      </div>
      <p className="text-xs text-bloq-muted">Translations land in your language. Turn Babel on everywhere, or per chat from the globe icon.</p>
      <div className="mt-4 flex items-center gap-3 rounded-2xl border border-white/8 px-4 py-3.5">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-white">My language</div>
          <div className="text-xs text-bloq-muted">Used for translations and Smart Replies</div>
        </div>
        {busy === "preferred_lang" ? <Loader2 className="h-4 w-4 animate-spin text-bloq-muted" /> : (
          <select
            value={lang}
            onChange={(e) => patch("preferred_lang", e.target.value, "Language updated")}
            className="rounded-xl border border-white/10 bg-bloq-s2 px-3 py-2 text-sm text-white outline-none focus:border-bloq-blue/50"
            data-testid="preferred-lang-select"
          >
            {LANGS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
          </select>
        )}
      </div>
      <div className="mt-2 flex items-center gap-3 rounded-2xl border border-white/8 px-4 py-3.5" data-testid="auto-translate-all-row">
        <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl", user?.auto_translate_all ? "bg-bloq-blue/15 text-bloq-blue" : "bg-white/5 text-bloq-muted")}><Globe className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-white">Auto-translate every chat</div>
          <div className="text-xs text-bloq-muted">Default for new and existing chats — a per-chat switch always wins</div>
        </div>
        {busy === "auto_translate_all" ? <Loader2 className="h-4 w-4 animate-spin text-bloq-muted" /> : (
          <Switch checked={!!user?.auto_translate_all} onCheckedChange={(v) => patch("auto_translate_all", v, v ? "Babel is on for every chat" : "Babel default off")} data-testid="auto-translate-all-toggle" />
        )}
      </div>
    </div>
  );
}

const PRIVACY = [
  { key: "read_receipts", icon: Eye, label: "Read receipts", sub: "Let people see when you've read their messages", def: true },
  { key: "last_seen", icon: EyeOff, label: "Last seen & online", sub: "Show when you were last active", def: true },
  { key: "public_reputation", icon: Fingerprint, label: "Public Trust Passport", sub: "Your /u/handle trust page is visible to anyone", def: true },
  { key: "allow_group_adds", icon: Users, label: "Let connections add me to Circles", sub: "Off = you get an invite instead", def: true },
  { key: "auto_open_connection_chat", icon: MessageSquare, label: "Open a chat when a request is accepted", sub: "Jump straight into the new DM", def: true },
  { key: "contact_discoverable", icon: Search, label: "Discoverable by phone contacts", sub: "Friends who sync their address book can find you", def: true },
  { key: "block_screenshots", icon: Camera, label: "Block screenshots (mobile)", sub: "Applies on your phone — the web can't enforce it", def: false },
];

// §16.1 Security-center privacy toggles (all PATCH /profile booleans).
export function PrivacyCard() {
  const { user } = useAuth();
  const { patch, busy } = useProfilePatch();
  return (
    <div id="privacy-card" className="rounded-3xl border border-white/8 bg-bloq-s1 p-5" data-testid="privacy-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
        <EyeOff className="h-4 w-4 text-bloq-mint" /> Privacy
      </div>
      <p className="text-xs text-bloq-muted">Synced with your phone — changes apply everywhere you're signed in.</p>
      <div className="mt-4 space-y-2">
        {PRIVACY.map((p) => {
          const on = user?.[p.key] ?? p.def;
          return (
            <div key={p.key} className="flex items-center gap-3 rounded-2xl border border-white/8 px-4 py-3" data-testid={`privacy-row-${p.key}`}>
              <span className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl", on ? "bg-bloq-mint/15 text-bloq-mint" : "bg-white/5 text-bloq-muted")}><p.icon className="h-4 w-4" /></span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-white">{p.label}</div>
                <div className="text-xs text-bloq-muted">{p.sub}</div>
              </div>
              {busy === p.key ? <Loader2 className="h-4 w-4 animate-spin text-bloq-muted" /> : (
                <Switch checked={!!on} onCheckedChange={(v) => patch(p.key, v)} data-testid={`privacy-toggle-${p.key}`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
